import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { FileListItemType } from '@views/documents/elements/FileList'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { getOrganizationViewUrl } from '../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { useDmsUserRole } from './useDmsUserRole'

export const useCopyDocumentOrFolderLink = () => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { isCpaExpertMode } = useDmsUserRole()

  const copyLink = useCallback(
    async (id: string, type: FileListItemType) => {
      if (!organization?.url) {
        return
      }

      // Use the route namespace matching the side the link is copied from (expert vs client), so
      // opening it doesn't flip an advisor's layer toggle away from where they currently are.
      const documentsNamespace = isCpaExpertMode
        ? reactRoute.organization.documents
        : reactRoute.organization.clientDocuments

      // Built from the item's stable ID (not its current path), so the link keeps working after a move.
      const route =
        type === 'document' ? documentsNamespace.getDocumentRoute(id) : documentsNamespace.getFolderRoute(id)
      const path = getOrganizationViewUrl(organization.url, route)
      const absoluteUrl = `${window.location.origin}${path}`

      try {
        await navigator.clipboard.writeText(absoluteUrl)
        notify({
          id: NotificationKeys.DocumentsCopyLink,
          message: t('documents.copy_link.notification.success'),
          variant: 'success',
        })
      } catch (error: unknown) {
        trackError(error)
        notify({
          id: NotificationKeys.DocumentsCopyLink,
          message: t('documents.copy_link.notification.error'),
          variant: 'error',
        })
      }
    },
    [isCpaExpertMode, organization?.url, t],
  )

  return { copyLink }
}
